import { Icon } from "@captions-web/icons";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { t } from "i18next";
import { useState } from "react";

import { LayoutSimple } from "~/components/layouts/simple";
import { Button } from "~/components/primitives/button";
import { DropdownMenu } from "~/components/primitives/dropdown-menu";
import { ElevatedBox } from "~/components/primitives/elevated-box";
import { textInputStyles } from "~/components/primitives/input";
import { Spinner } from "~/components/primitives/spinner";
import { TeamEvents } from "~/lib/analytics/defs/TeamEvents";
import { getAuthStore } from "~/modules/auth/auth-store";
import {
  canUserDeleteMembers,
  canUserEditOrgInfo,
  canUserInviteMembers,
  canUserModifyRoles,
  canUserRevokeInvites,
} from "~/modules/auth/permissions";
import { InviteDialog } from "~/modules/teams/components/invite-dialog";
import {
  removeMemberMutation,
  revokeInviteMutation,
  updateMemberMutation,
  updateOrgMutation,
} from "~/modules/teams/network/mutations";
import { orgDetailsQuery } from "~/modules/teams/network/queries";
import { Member, PendingMember } from "~/modules/teams/network/schema";
import { css } from "~/styled-system/css";

export const Route = createFileRoute("/_app/settings/team")({
  component: RouteComponent,
  pendingComponent: PendingComponent,
  async loader({ context: { queryClient } }) {
    await queryClient.ensureQueryData(orgDetailsQuery.queryOptions());
  },
});

function PendingComponent() {
  return null;
}

function RouteComponent() {
  const { org, user } = Route.useRouteContext();
  const { data } = orgDetailsQuery.useSuspenseQuery();
  const members = data.orgProfile.members.toSorted((a, b) => {
    if (b.userId === user.userId) {
      return 1;
    } else if (a.userId === user.userId) {
      return -1;
    } else {
      return a.name.localeCompare(b.name);
    }
  });
  const pendingMembers = data.orgProfile.pendingMembers;
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const router = useRouter();
  const {
    mutate: mutateUpdateOrg,
    isPending,
    error,
  } = updateOrgMutation.useMutation({
    onSettled() {
      // refresh { org } with latest store value in the route context
      router.invalidate();
    },
    onSuccess(_, variables) {
      getAuthStore().refreshProfileAfterOrgUpdate({
        name: variables.params.name,
        thumbnailUrl: variables.params.logoImageUrl,
      });

      if (variables.params.name) {
        TeamEvents.name_change_status.track({
          status: "success",
        });
      }
    },
    onError(error, variables) {
      if (variables.params.name) {
        TeamEvents.name_change_status.track({
          status: "error",
          error_message: error.message,
        });
      }
    },
  });

  const updateName = (name: string) => {
    const nameChanged = name !== org.name;
    if (nameChanged) {
      mutateUpdateOrg({ params: { name, orgId: org.id } });
    }
  };

  return (
    <LayoutSimple title={t("mirage-studio:sidebar.settings.team")}>
      <ElevatedBox.Root withDividers>
        <ElevatedBox.Item className={css({ justifyContent: "space-between", gap: 8 })}>
          <div>
            {data.orgProfile.thumbnailUrl && (
              <img src={data.orgProfile.thumbnailUrl} className={orgThumbnailStyles} alt="" />
            )}
            <span className={css({ textStyle: "body-2-intense", color: "$gray.200" })}>
              {data.orgProfile.name}
            </span>
          </div>
          <span className={css({ textStyle: "body-2-subtle", color: "$gray.500" })}>
            {t(`mirage-studio:teams.members_${members.length === 1 ? "one" : "other"}`, {
              count: members.length,
            })}
          </span>
        </ElevatedBox.Item>
        <ElevatedBox.Item className={css({ justifyContent: "space-between", gap: 8 })}>
          <span className={css({ textStyle: "body-2-intense", color: "$gray.200" })}>
            {t("mirage-studio:teams.name")}
          </span>
          <div className={css({ display: "flex", gap: 8, alignItems: "center" })}>
            {isPending && <Spinner />}
            {error && (
              <span className={css({ color: "$error" })}>
                {t("mirage-studio:teams.error-updating-name")}
              </span>
            )}
            <input
              type="text"
              className={textInputStyles}
              defaultValue={org.name}
              disabled={isPending || !canUserEditOrgInfo(user)}
              onBlur={(event) => updateName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  updateName(event.currentTarget.value);
                }
              }}
            />
          </div>
        </ElevatedBox.Item>
      </ElevatedBox.Root>

      <div className={sectionHeaderStyles}>
        <span>{t("mirage-studio:teams.people")}</span>
        {canUserInviteMembers(user) && (
          <Button icon={<Icon name="add" />} size="md" onClick={() => setInviteDialogOpen(true)}>
            {t("mirage-studio:teams.invite-members")}
          </Button>
        )}
      </div>

      <ElevatedBox.Root>
        {!!pendingMembers.length && (
          <ElevatedBox.Item className={elevatedSectionHeaderTextStyles}>
            <span>{t("mirage-studio:teams.invited")}</span>
            <span>({pendingMembers.length})</span>
          </ElevatedBox.Item>
        )}

        {pendingMembers.map((pendingMember) => (
          <PendingMemberItem key={pendingMember.email} pendingMember={pendingMember} />
        ))}

        {!!members.length && (
          <ElevatedBox.Item className={elevatedSectionHeaderTextStyles}>
            <span>
              {t(`mirage-studio:teams.members_${members.length === 1 ? "one" : "other"}`, {
                count: members.length,
              })}
            </span>
            <span>({members.length})</span>
          </ElevatedBox.Item>
        )}

        {members.map((member) => (
          <MemberItem key={member.userId} member={member} />
        ))}
      </ElevatedBox.Root>
      <InviteDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        permissionsMode={canUserModifyRoles(user) ? "admin" : "member"}
      />
    </LayoutSimple>
  );
}

interface MemberItemProps {
  member: Member;
}

function MemberItem({ member }: MemberItemProps) {
  const { user } = Route.useRouteContext();
  const isYou = member.userId === user.userId;

  const { mutate: mutateRole, isPending: roleIsPending } = updateMemberMutation.useMutation({
    onSuccess(_, variables) {
      TeamEvents.change_member_role_status.track({
        status: "success",
        role: variables.params.role,
      });
    },
    onError(error, variables) {
      TeamEvents.change_member_role_status.track({
        status: "error",
        role: variables.params.role,
        error_message: error.message,
      });
    },
  });

  const { mutate: mutateRemove, isPending: removeIsPending } = removeMemberMutation.useMutation({
    onSuccess() {
      TeamEvents.remove_member_status.track({
        status: "success",
        role: member.role,
      });
    },
    onError(error) {
      TeamEvents.remove_member_status.track({
        status: "error",
        role: member.role,
        error_message: error.message,
      });
    },
  });

  const dropdownItems: React.ReactNode[] = [];
  if (canUserDeleteMembers(user) && !isYou) {
    dropdownItems.push(
      <DropdownMenu.Item
        key="remove-member"
        icon={<Icon name="delete-bare" />}
        onClick={() => mutateRemove({ params: { userId: member.userId } })}
        disabled={removeIsPending}
        destructive
      >
        {t("mirage-studio:teams.remove-member")}
      </DropdownMenu.Item>
    );
  }

  return (
    <ElevatedBox.Item className={css({ justifyContent: "space-between", gap: 8 })}>
      <div className={css({ display: "flex", gap: "8px", alignItems: "center" })}>
        <Avatar name={member.name} />
        <div className={css({ display: "flex", flexDirection: "column" })}>
          <span>
            {member.name || member.email} {isYou && `(${t("mirage-studio:teams.you")})`}
          </span>
          {member.name && (
            <span className={css({ textStyle: "body-3-subtle", color: "$gray.500" })}>
              {member.email}
            </span>
          )}
        </div>
      </div>
      <div className={css({ display: "flex", gap: "8px", alignItems: "center" })}>
        {isYou || !canUserModifyRoles(user) ? (
          <span
            className={css({
              textStyle: "body-3-subtle",
              color: "$gray.500",
              textTransform: "capitalize",
            })}
          >
            {member.role}
          </span>
        ) : (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button
                variant="elevated"
                iconPosition="after"
                icon={<Icon name="chevron-down" />}
                isPending={roleIsPending}
              >
                <span className={css({ textTransform: "capitalize" })}>{member.role}</span>
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content sideOffset={5} align="end">
              <DropdownMenu.Item
                onClick={() => mutateRole({ params: { userId: member.userId, role: "admin" } })}
              >
                {t("mirage-studio:teams.admin")}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onClick={() => mutateRole({ params: { userId: member.userId, role: "member" } })}
              >
                {t("mirage-studio:teams.member")}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )}

        {dropdownItems.length > 0 && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button variant="quarternary" iconPosition="only" icon={<Icon name="options" />} />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content sideOffset={5} align="end">
              {dropdownItems}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )}
      </div>
    </ElevatedBox.Item>
  );
}

interface PendingMemberItemProps {
  pendingMember: PendingMember;
}

function PendingMemberItem({ pendingMember }: PendingMemberItemProps) {
  const { user } = Route.useRouteContext();

  const dropdownItems: React.ReactNode[] = [];
  // TODO: bring back once resend invite endpoint is not broken
  // if (canUserInviteMembers(user)) {
  //   dropdownItems.push(
  //     <DropdownMenu.Item
  //       key="resend-invite"
  //       icon={<Icon name="email" />}
  //       onClick={() => {
  //         // We don't know what role this user was invited with, so for now default to "member"
  //         inviteMemberMutation.trigger({ params: { email: pendingMember.email, role: "member" } });
  //       }}
  //     >
  //       {t("mirage-studio:teams.resend-invitation")}
  //     </DropdownMenu.Item>
  //   );
  // }

  const { mutate: mutateRevoke, status: revokeStatus } = revokeInviteMutation.useMutation({
    onSuccess() {
      TeamEvents.revoke_invite_status.track({
        status: "success",
      });
    },
    onError(error) {
      TeamEvents.revoke_invite_status.track({
        status: "error",
        error_message: error.message,
      });
    },
  });

  if (canUserRevokeInvites(user)) {
    dropdownItems.push(
      <DropdownMenu.Item
        key="remove-member"
        icon={<Icon name="delete-bare" />}
        onClick={() => mutateRevoke({ params: { email: pendingMember.email } })}
        disabled={revokeStatus === "pending" || revokeStatus === "success"}
        destructive
      >
        {t("mirage-studio:teams.remove-member")}
      </DropdownMenu.Item>
    );
  }

  return (
    <ElevatedBox.Item className={css({ justifyContent: "space-between", gap: 8 })}>
      <div className={css({ display: "flex", gap: "8px", alignItems: "center" })}>
        <Avatar name={pendingMember.email} />
        <div className={css({ display: "flex", flexDirection: "column" })}>
          <span>{pendingMember.email}</span>
        </div>
      </div>
      <div className={css({ display: "flex", gap: "8px", alignItems: "center" })}>
        {revokeStatus === "pending" || revokeStatus === "success" ? (
          <Spinner />
        ) : (
          <span className={css({ textStyle: "body-3-subtle", color: "$gray.500" })}>
            {t("mirage-studio:teams.invite-sent")}
          </span>
        )}
        {dropdownItems.length > 0 && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button variant="quarternary" iconPosition="only" icon={<Icon name="options" />} />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content sideOffset={5} align="end">
              {dropdownItems}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )}
      </div>
    </ElevatedBox.Item>
  );
}

export interface PersonAvatarProps {
  name?: string;
}

const AVATAR_COLORS = [
  ["#40CDA0", "#85E1A2"],
  ["#FB8F68", "#FFC333"],
  ["#4D72F7", "#4D9FF7"],
  ["#0E6066", "#15B0BD"],
  ["#6108CC", "#7E12FF"],
  ["#89D229", "#AAFF4E"],
  ["#AB88FB", "#B19FFF"],
] as const;

function getColorsForName(name: string) {
  const hash = Array.from(name).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const index = hash % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

export function Avatar({ name }: PersonAvatarProps) {
  const [color1, color2] = getColorsForName(name ?? "");
  return (
    <div
      className={css({
        width: 24,
        height: 24,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        fontSize: 12,
        fontWeight: 500,
        textTransform: "uppercase",
        boxShadow: "$highlight",
      })}
      style={{ background: `linear-gradient(180deg, ${color1}, ${color2})` }}
    >
      {name?.at(0)}
    </div>
  );
}

const orgThumbnailStyles = css({ width: 32, height: 32, borderRadius: "50%" });

const sectionHeaderStyles = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
});

const elevatedSectionHeaderTextStyles = css({
  justifyContent: "space-between",
  color: "$gray.500",
  textStyle: "body-3-caps",
  gap: 8,
});
